/**
 * Mollie payment integration (client-side shape only).
 *
 * In production a payment is NEVER created from the browser: the Mollie API key
 * must stay on a server. The real flow is:
 *
 *   1. This client POSTs the order to your backend (`/api/payments`).
 *   2. The backend calls Mollie `POST /v2/payments` with the secret key and
 *      returns `{ id, status, checkoutUrl }`.
 *   3. The client redirects the customer to `checkoutUrl` (Mollie hosted) or,
 *      with Mollie Components, tokenises the card fields and confirms here.
 *   4. Mollie calls the backend webhook with the final status; the backend
 *      marks the order as paid.
 *
 * This module keeps that exact contract so only `requestPayment` needs to be
 * pointed at the real endpoint later. For now it resolves locally so the
 * checkout can be reviewed end to end.
 */
export type PaymentStatus = 'open' | 'pending' | 'paid' | 'failed'
export type PaymentMethod = 'creditcard' | 'applepay' | 'googlepay'
export interface PaymentCustomer {
  name: string
  email: string
  phone: string
}
export interface PaymentRequest {
  /** Amount in euros, e.g. 350 */
  amount: number
  currency: 'EUR'
  description: string
  orderReference: string
  method: PaymentMethod
  customer: PaymentCustomer
}
export interface PaymentResult {
  id: string
  status: PaymentStatus
  method: PaymentMethod
  /** Hosted Mollie page the customer is redirected to in production. */
  checkoutUrl: string
  orderReference: string
}
const PAYMENTS_ENDPOINT = '/api/payments'
function buildLocalResult(request: PaymentRequest): PaymentResult {
  const id = `tr_${Math.random().toString(36).slice(2, 12)}`
  return {
    id,
    status: 'paid',
    method: request.method,
    checkoutUrl: `https://www.mollie.com/checkout/select-method/${id}`,
    orderReference: request.orderReference,
  }
}
export async function requestPayment(
  request: PaymentRequest,
): Promise<PaymentResult> {
  try {
    const response = await fetch(PAYMENTS_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })
    if (!response.ok) throw new Error(`Payment endpoint error ${response.status}`)
    return (await response.json()) as PaymentResult
  } catch (error) {
    // No backend wired yet — resolve with the same contract so the flow is reviewable.
    await new Promise((resolve) => setTimeout(resolve, 1100))
    return buildLocalResult(request)
  }
}
export function formatEuro(amount: number): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(amount)
}
export function createOrderReference(): string {
  return String(1800 + Math.floor(Math.random() * 199))
}
